/**
 * AdminShipping.js - Manage shipping rates and settings
 * Author: Markus.van.Kempen@gmail.com
 * Date: November 11, 2025
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useToast } from './ToastContainer';
import { parseWeightThresholdsForEdit } from '../utils/shippingCanadaPost';
import { exportShippingTaxExcel, exportQuickSimulator } from '../utils/exportShippingTaxExcel';
import {
  ESTIMATE_BOOK_KG,
  ESTIMATE_CD_KG,
  ESTIMATE_BOOK_PRICE,
  ESTIMATE_CD_PRICE,
  estimateCartWeightKg,
  estimateCartMerchandise
} from '../utils/shippingEstimateDefaults';
import { DEFAULT_CURRENCY_RATES, fetchCurrencyRates, invalidateCurrencyRatesCache } from '../hooks/useCurrencyRates';
import './AdminShipping.css';

const DEFAULT_FLAT_RATE_CONFIG = {
  enabled: false,
  fuel_surcharge_pct: 0,
  transaction_fee_pct: 0,
  destinations: {
    CA:   {
      Book: { handling_cents: 1100, per_item_cents: 200, tiers: [{ up_to: 2, per_item_cents: 200 }, { up_to: 5, per_item_cents: 350 }, { up_to: null, per_item_cents: 300 }] },
      CD:   { handling_cents: 500,  per_item_cents: 150, tiers: [{ up_to: 2, per_item_cents: 150 }, { up_to: null, per_item_cents: 200 }] }
    },
    US:   {
      Book: { handling_cents: 1500, per_item_cents: 300, tiers: [{ up_to: 2, per_item_cents: 300 }, { up_to: 5, per_item_cents: 600 }, { up_to: null, per_item_cents: 500 }] },
      CD:   { handling_cents: 500,  per_item_cents: 150, tiers: [{ up_to: 2, per_item_cents: 150 }, { up_to: null, per_item_cents: 250 }] }
    },
    INTL: {
      Book: { handling_cents: 2000, per_item_cents: 500, tiers: [{ up_to: 2, per_item_cents: 500 }, { up_to: 5, per_item_cents: 1200 }, { up_to: null, per_item_cents: 1000 }] },
      CD:   { handling_cents: 800,  per_item_cents: 250, tiers: [{ up_to: 2, per_item_cents: 250 }, { up_to: null, per_item_cents: 500 }] }
    }
  }
};

function parseFlatRateConfig(raw) {
  if (raw == null) return null;
  if (typeof raw === 'object') return raw;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw); } catch { return null; }
  }
  return null;
}

function loadFlatRateFromSettings(settings) {
  for (const s of settings || []) {
    const parsed = parseFlatRateConfig(s.flat_rate_config);
    if (parsed) return parsed;
  }
  return null;
}

const AdminShipping = () => {
  const [shippingSettings, setShippingSettings] = useState([]);
  const [taxSettings, setTaxSettings] = useState([]);
  const [activeTab, setActiveTab] = useState('shipping');
  const [activeCountryTab, setActiveCountryTab] = useState('CA'); // New state for country tabs
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingCountry, setEditingCountry] = useState(null);
  const [editingTax, setEditingTax] = useState(null);
  const [flatRateEditing, setFlatRateEditing] = useState(false);
  const [flatRateData, setFlatRateData] = useState(null);
  const [exampleBooks, setExampleBooks] = useState(3);
  const [exampleCds, setExampleCds] = useState(1);
  const [currencyRates, setCurrencyRates] = useState(DEFAULT_CURRENCY_RATES);
  const [currencyRatesEditing, setCurrencyRatesEditing] = useState(false);
  const [currencyRatesDraft, setCurrencyRatesDraft] = useState(DEFAULT_CURRENCY_RATES);
  const { showSuccess, showError } = useToast();

  const fetchShippingSettings = useCallback(async () => {
    try {
      const [shippingRes, taxRes] = await Promise.all([
        fetch('/api/shipping-settings'),
        fetch('/api/tax-settings')
      ]);

      if (!shippingRes.ok) throw new Error(`Shipping API error: ${shippingRes.status}`);
      if (!taxRes.ok) throw new Error(`Tax API error: ${taxRes.status}`);

      const shippingData = await shippingRes.json();
      const taxData = await taxRes.json();

      // Parse weight_thresholds if they're strings
      const parsedShippingData = shippingData.map(setting => {
        // Debug: log the raw setting data
        // console.log('📦 Shipping setting for', setting.country_code, ':', { ... });

        if (setting.weight_thresholds) {
          if (typeof setting.weight_thresholds === 'string') {
            try {
              setting.weight_thresholds = JSON.parse(setting.weight_thresholds);
            } catch (e) {
              console.warn('Failed to parse weight_thresholds for', setting.country_code, ':', e);
              setting.weight_thresholds = [];
            }
          }
        } else {
          setting.weight_thresholds = [];
        }

        setting.flat_rate_config = parseFlatRateConfig(setting.flat_rate_config);
        return setting;
      });

      setShippingSettings(parsedShippingData);
      setTaxSettings(taxData);
      setFlatRateData(loadFlatRateFromSettings(parsedShippingData) || DEFAULT_FLAT_RATE_CONFIG);

      const rates = await fetchCurrencyRates();
      setCurrencyRates(rates);
      setCurrencyRatesDraft(rates);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching settings:', error);
      showError('Failed to load settings: ' + error.message);
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchShippingSettings();
  }, [fetchShippingSettings]);

  const handleCurrencyRatesEdit = () => {
    setCurrencyRatesDraft({ ...currencyRates });
    setCurrencyRatesEditing(true);
  };

  const handleCurrencyRatesCancel = () => {
    setCurrencyRatesDraft({ ...currencyRates });
    setCurrencyRatesEditing(false);
  };

  const handleCurrencyRatesSave = async () => {
    setSaving(true);
    try {
      const response = await fetch('/api/currency-rates', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          cadToUsdRate: parseFloat(currencyRatesDraft.cadToUsdRate),
          cadToEurRate: parseFloat(currencyRatesDraft.cadToEurRate),
          cadToGbpRate: parseFloat(currencyRatesDraft.cadToGbpRate),
          cadToAudRate: parseFloat(currencyRatesDraft.cadToAudRate),
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `HTTP error! status: ${response.status}`);
      }

      const updated = await response.json();
      invalidateCurrencyRatesCache();
      setCurrencyRates(updated);
      setCurrencyRatesDraft(updated);
      setCurrencyRatesEditing(false);
      showSuccess('Reference exchange rates updated');
    } catch (error) {
      console.error('Error saving currency rates:', error);
      showError('Failed to save exchange rates: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (setting) => {
    const rawWt = setting.weight_thresholds;
    const { bands, caPostExtras } = parseWeightThresholdsForEdit(rawWt);

    setEditingCountry({
      ...setting,
      standard_rate: parseFloat(setting.standard_rate),
      expedited_rate: parseFloat(setting.expedited_rate),
      express_rate: parseFloat(setting.express_rate),
      free_threshold: parseFloat(setting.free_threshold),
      enabled: Boolean(setting.enabled),
      weight_thresholds: bands,
      caPostExtras: setting.country_code === 'CA' ? caPostExtras : undefined,
      default_weight_kg: parseFloat(setting.default_weight_kg) || 0.1,
      use_default_weight: setting.use_default_weight !== false,
      min_items_for_weight: parseInt(setting.min_items_for_weight) || 1,
      tariff_enabled: Boolean(setting.tariff_enabled),
      tariff_rate: parseFloat(setting.tariff_rate) || 0.35,
      tariff_exemption_threshold_usd: parseFloat(setting.tariff_exemption_threshold_usd) || 100.00,
      tariff_notes: setting.tariff_notes || ''
    });
  };

  const handleSave = async () => {
    if (!editingCountry) return;

    setSaving(true);
    try {
      let weightPayload = editingCountry.weight_thresholds || [];
      if (editingCountry.country_code === 'CA' && editingCountry.caPostExtras) {
        weightPayload = {
          lettermail_bands: editingCountry.weight_thresholds || [],
          ...editingCountry.caPostExtras
        };
      }

      const response = await fetch(`/api/shipping-settings/${editingCountry.country_code}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          standard_rate: parseFloat(editingCountry.standard_rate),
          expedited_rate: parseFloat(editingCountry.expedited_rate),
          express_rate: parseFloat(editingCountry.express_rate),
          free_threshold: parseFloat(editingCountry.free_threshold),
          enabled: editingCountry.enabled,
          weight_thresholds: weightPayload,
          default_weight_kg: parseFloat(editingCountry.default_weight_kg) || 0.1,
          use_default_weight: editingCountry.use_default_weight !== false,
          min_items_for_weight: parseInt(editingCountry.min_items_for_weight) || 1,
          tariff_enabled: editingCountry.tariff_enabled,
          tariff_rate: parseFloat(editingCountry.tariff_rate) || 0.35,
          tariff_exemption_threshold_usd: parseFloat(editingCountry.tariff_exemption_threshold_usd) || 100.00,
          tariff_notes: editingCountry.tariff_notes || '',
          flat_rate_config: flatRateData
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updated = await response.json();

      // Ensure weight_thresholds is parsed if it's a string
      if (updated.weight_thresholds) {
        if (typeof updated.weight_thresholds === 'string') {
          try {
            updated.weight_thresholds = JSON.parse(updated.weight_thresholds);
          } catch (e) {
            console.warn('Failed to parse weight_thresholds:', e);
            updated.weight_thresholds = [];
          }
        }
      } else {
        // If weight_thresholds is null/undefined, set to empty array
        updated.weight_thresholds = [];
      }

      console.log('✅ Updated shipping settings:', updated);
      console.log('✅ Weight thresholds:', updated.weight_thresholds);

      setShippingSettings(prev =>
        prev.map(s => s.country_code === updated.country_code ? updated : s)
      );

      showSuccess(`✅ ${editingCountry.country_name} shipping rates updated`);
      setEditingCountry(null);
      setSaving(false);
    } catch (error) {
      console.error('Error saving shipping settings:', error);
      showError('Failed to save: ' + error.message);
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditingCountry(null);
  };

  // Flat-rate tab: update nested fields on the standalone flatRateData state
  const handleFlatRateTabChange = (path, value) => {
    setFlatRateData(prev => {
      const config = JSON.parse(JSON.stringify(prev || {}));
      const keys = path.split('.');
      let obj = config;
      for (let i = 0; i < keys.length - 1; i++) {
        if (!obj[keys[i]]) obj[keys[i]] = {};
        obj = obj[keys[i]];
      }
      obj[keys[keys.length - 1]] = value;
      return config;
    });
  };

  // Save flat-rate config to ALL shipping_settings rows
  const handleFlatRateSave = async () => {
    setSaving(true);
    try {
      const promises = shippingSettings.map(s =>
        fetch(`/api/shipping-settings/${s.country_code}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            standard_rate: s.standard_rate,
            expedited_rate: s.expedited_rate,
            express_rate: s.express_rate,
            free_threshold: s.free_threshold,
            enabled: s.enabled,
            weight_thresholds: s.weight_thresholds,
            default_weight_kg: s.default_weight_kg,
            use_default_weight: s.use_default_weight,
            min_items_for_weight: s.min_items_for_weight,
            tariff_enabled: s.tariff_enabled,
            tariff_rate: s.tariff_rate,
            tariff_exemption_threshold_usd: s.tariff_exemption_threshold_usd,
            tariff_notes: s.tariff_notes,
            flat_rate_config: flatRateData
          })
        })
      );
      const results = await Promise.all(promises);
      const failed = await Promise.all(
        results.map(async (r, i) => {
          if (r.ok) return null;
          let msg = r.statusText;
          try {
            const body = await r.json();
            msg = body.error || body.message || msg;
          } catch { /* ignore */ }
          return { country: shippingSettings[i]?.country_code, status: r.status, msg };
        })
      );
      const failures = failed.filter(Boolean);
      if (failures.length > 0) {
        const authFailed = failures.some(f => f.status === 401 || f.status === 403);
        const detail = failures.map(f => `${f.country}: ${f.msg}`).join('; ');
        throw new Error(
          authFailed
            ? `Not authorized — please log in again and retry. (${detail})`
            : `Failed to save: ${detail}`
        );
      }

      showSuccess('Flat-rate shipping settings saved');
      setFlatRateEditing(false);
      // Keep UI in sync immediately; refetch to refresh shippingSettings too
      setFlatRateData(flatRateData);
      await fetchShippingSettings();
    } catch (err) {
      console.error('Error saving flat-rate config:', err);
      showError(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleCaPostExtraChange = (field, value) => {
    setEditingCountry(prev => ({
      ...prev,
      caPostExtras: {
        ...(prev.caPostExtras || {}),
        [field]: value
      }
    }));
  };

  const handleInputChange = (field, value) => {
    setEditingCountry(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const addWeightThreshold = () => {
    setEditingCountry(prev => ({
      ...prev,
      weight_thresholds: [
        ...(prev.weight_thresholds || []),
        { weight_kg: 0.2, price_cents: 2500 } // Default: 200g = $25
      ]
    }));
  };

  const updateWeightThreshold = (index, field, value) => {
    setEditingCountry(prev => {
      const thresholds = [...(prev.weight_thresholds || [])];
      thresholds[index] = {
        ...thresholds[index],
        [field]: field === 'price_cents' ? parseFloat(value) * 100 : parseFloat(value)
      };
      return { ...prev, weight_thresholds: thresholds };
    });
  };

  const removeWeightThreshold = (index) => {
    setEditingCountry(prev => {
      const thresholds = [...(prev.weight_thresholds || [])];
      thresholds.splice(index, 1);
      return { ...prev, weight_thresholds: thresholds };
    });
  };

  // Tax Handlers
  const handleTaxEdit = (tax) => {
    setEditingTax({ ...tax });
  };

  const handleTaxCancel = () => {
    setEditingTax(null);
  };

  const handleTaxInputChange = (field, value) => {
    setEditingTax(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTaxSave = async () => {
    if (!editingTax) return;

    setSaving(true);
    try {
      const response = await fetch(`/api/tax-settings/${editingTax.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          general_rate: parseFloat(editingTax.general_rate),
          books_rate: parseFloat(editingTax.books_rate),
          shipping_tax_rate: parseFloat(editingTax.shipping_tax_rate),
          notes: editingTax.notes,
          enabled: editingTax.enabled
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const updated = await response.json();

      setTaxSettings(prev =>
        prev.map(t => t.id === updated.id ? updated : t)
      );

      showSuccess(`✅ Tax settings for ${updated.province_name} updated`);
      setEditingTax(null);
      setSaving(false);
    } catch (error) {
      console.error('Error saving tax settings:', error);
      showError('Failed to save tax settings: ' + error.message);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-shipping">
        <div className="loading-spinner">Loading shipping settings...</div>
      </div>
    );
  }

  return (
    <div className="admin-shipping">
      <div className="admin-shipping-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2>🚚 Shipping & Tax Settings</h2>
            <p className="header-subtitle">
              Configure shipping rates and tax rules for different regions.
            </p>
          </div>
          <div className="export-actions">
            <button
              type="button"
              className="export-btn"
              onClick={async () => {
                try {
                  await exportQuickSimulator();
                  showSuccess('Quick Simulator exported');
                } catch (err) {
                  console.error('Export failed:', err);
                  showError(`Export failed: ${err.message}`);
                }
              }}
              title="Export current active settings + interactive simulator for validation"
            >
              Quick Simulator
            </button>
            <button
              type="button"
              className="export-btn"
              onClick={async () => {
                try {
                  await exportShippingTaxExcel();
                  showSuccess('Full Excel workbook exported');
                } catch (err) {
                  console.error('Export failed:', err);
                  showError(`Export failed: ${err.message}`);
                }
              }}
              title="Export all shipping rates, tax rates, tariff config, and a simulator template to an Excel workbook"
            >
              Full Export
            </button>
          </div>
        </div>
      </div>

      {/* Main Tabs - Shipping vs Tax */}
      <div className="admin-tabs">
        <button
          type="button"
          className={`tab-btn ${activeTab === 'shipping' ? 'active' : ''}`}
          onClick={() => setActiveTab('shipping')}
        >
          🚚 Shipping Rates
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === 'flat-rate' ? 'active' : ''}`}
          onClick={() => setActiveTab('flat-rate')}
        >
          📦 Flat-Rate
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === 'exchange-rates' ? 'active' : ''}`}
          onClick={() => setActiveTab('exchange-rates')}
        >
          💱 Exchange Rates
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === 'tax' ? 'active' : ''}`}
          onClick={() => setActiveTab('tax')}
        >
          💰 Tax Configuration
        </button>
      </div>

      {activeTab === 'shipping' ? (
        <>
          {/* Country Tabs */}
          <div className="country-tabs">
            {shippingSettings.map(setting => (
              <button
                type="button"
                key={setting.country_code}
                className={`country-tab-btn ${activeCountryTab === setting.country_code ? 'active' : ''} ${!setting.enabled ? 'is-disabled-country' : ''}`}
                onClick={() => setActiveCountryTab(setting.country_code)}
              >
                <span style={{ fontSize: '1.2rem', marginRight: '6px' }}>
                  {getCountryFlag(setting.country_code)}
                </span>
                {setting.country_name}
                {!setting.enabled && (
                  <span style={{ fontSize: '0.7rem', marginLeft: '6px', opacity: 0.7 }}>
                    (Disabled)
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Display only the selected country */}
          <div className="shipping-settings-container" style={{ width: '100%' }}>
            {shippingSettings
              .filter(setting => setting.country_code === activeCountryTab)
              .map(setting => (
                <div
                  key={setting.country_code}
                  className={`shipping-card ${!setting.enabled ? 'disabled' : ''} ${editingCountry?.country_code === setting.country_code ? 'editing' : ''}`}
                  style={{ maxWidth: '900px', width: '100%', margin: '0 auto' }}
                >
                  <div className="shipping-card-header">
                    <h3>
                      <span className="country-flag">{getCountryFlag(setting.country_code)}</span>
                      {setting.country_name}
                      <span className="country-code">({setting.country_code})</span>
                    </h3>
                    {!setting.enabled && <span className="disabled-badge">Disabled</span>}
                  </div>

                  {editingCountry?.country_code === setting.country_code ? (
                    // Edit Mode
                    <div className="shipping-edit-form">
                      <div className="form-row">
                        <label>
                          <span className="label-text">Standard Shipping:</span>
                          <div className="input-with-prefix">
                            <span className="prefix">$</span>
                            <input
                              type="number"
                              step="0.01"
                              value={editingCountry.standard_rate}
                              onChange={(e) => handleInputChange('standard_rate', e.target.value)}
                              className="rate-input"
                            />
                          </div>
                        </label>
                      </div>

                      <div className="form-row">
                        <label>
                          <span className="label-text">Expedited Shipping:</span>
                          <div className="input-with-prefix">
                            <span className="prefix">$</span>
                            <input
                              type="number"
                              step="0.01"
                              value={editingCountry.expedited_rate}
                              onChange={(e) => handleInputChange('expedited_rate', e.target.value)}
                              className="rate-input"
                            />
                          </div>
                        </label>
                      </div>

                      <div className="form-row">
                        <label>
                          <span className="label-text">Express Shipping:</span>
                          <div className="input-with-prefix">
                            <span className="prefix">$</span>
                            <input
                              type="number"
                              step="0.01"
                              value={editingCountry.express_rate}
                              onChange={(e) => handleInputChange('express_rate', e.target.value)}
                              className="rate-input"
                            />
                          </div>
                        </label>
                      </div>

                      <div className="form-row free-threshold-row">
                        <label>
                          <span className="label-text">🎁 Free Shipping Threshold:</span>
                          <div className="input-with-prefix">
                            <span className="prefix">$</span>
                            <input
                              type="number"
                              step="0.01"
                              value={editingCountry.free_threshold}
                              onChange={(e) => handleInputChange('free_threshold', e.target.value)}
                              className="rate-input threshold-input"
                            />
                          </div>
                          <span className="help-text">
                            Orders over this amount ship free
                          </span>
                        </label>
                      </div>

                      <div className="form-row">
                        <label>
                          <span className="label-text">⚖️ Default Product Weight:</span>
                          <div className="input-with-prefix">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={editingCountry.default_weight_kg || 0.1}
                              onChange={(e) => handleInputChange('default_weight_kg', e.target.value)}
                              className="rate-input"
                            />
                            <span className="suffix">kg</span>
                          </div>
                          <span className="help-text">
                            Default weight for products without a specified weight
                          </span>
                        </label>
                      </div>

                      <div className="form-row">
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={editingCountry.use_default_weight !== false}
                            onChange={(e) => handleInputChange('use_default_weight', e.target.checked)}
                          />
                          <span className="label-text">Use default weight for products without weight</span>
                          <span className="help-text">
                            When enabled, products without a weight will use the default weight above for shipping calculations
                          </span>
                        </label>
                      </div>

                      <div className="form-row">
                        <label>
                          <span className="label-text">📦 Minimum Items for Weight Calculation:</span>
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={editingCountry.min_items_for_weight || 1}
                            onChange={(e) => handleInputChange('min_items_for_weight', e.target.value)}
                            className="rate-input"
                            style={{ maxWidth: '150px' }}
                          />
                          <span className="help-text">
                            Weight-based shipping only applies when cart has this many items or more (use with default weight to override product minimums)
                          </span>
                        </label>
                      </div>

                      <div className="form-row weight-thresholds-row">
                        <label>
                          <span className="label-text">⚖️ Weight-Based Shipping Thresholds:</span>
                          <span className="help-text" style={{ display: 'block', marginBottom: '10px' }}>
                            Set fixed shipping prices based on total order weight. If order weight exceeds a threshold, use that price.
                          </span>
                          <div className="weight-thresholds-list">
                            {(editingCountry.weight_thresholds || []).map((threshold, index) => (
                              <div key={index} className="weight-threshold-item">
                                <div className="threshold-input-group">
                                  <label>
                                    <span>Up to</span>
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      value={threshold.weight_kg}
                                      onChange={(e) => updateWeightThreshold(index, 'weight_kg', e.target.value)}
                                      className="threshold-input"
                                      placeholder="0.2"
                                    />
                                    <span>kg</span>
                                  </label>
                                </div>
                                <div className="threshold-input-group">
                                  <label>
                                    <span>Price:</span>
                                    <div className="input-with-prefix">
                                      <span className="prefix">$</span>
                                      <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={(threshold.price_cents / 100).toFixed(2)}
                                        onChange={(e) => updateWeightThreshold(index, 'price_cents', e.target.value)}
                                        className="threshold-input"
                                        placeholder="25.00"
                                      />
                                    </div>
                                  </label>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => removeWeightThreshold(index)}
                                  className="btn-remove-threshold"
                                  title="Remove threshold"
                                >
                                  ✕
                                </button>
                              </div>
                            ))}
                            <button
                              type="button"
                              onClick={addWeightThreshold}
                              className="btn-add-threshold"
                            >
                              + Add Weight Threshold
                            </button>
                          </div>
                          <span className="help-text" style={{ display: 'block', marginTop: '10px', fontSize: '0.85em' }}>
                            {editingCountry.country_code === 'CA' && editingCountry.caPostExtras ? (
                              <>
                                Canada: <strong>Oversized Lettermail</strong> (max 500g). Standard shipping also picks the cheapest eligible <strong>prepaid flat-rate box</strong> (by max box length in JSON and <code>ca_shipment_longest_cm</code> for legal-size booklets) vs lettermail when both apply.
                              </>
                            ) : (
                              <>
                                Example: Up to 0.2kg one price; up to 0.3kg another. The first threshold that covers total cart weight applies.
                              </>
                            )}
                          </span>
                        </label>
                      </div>

                      {/* Canada Post Fuel Surcharge - Only for CA */}
                      {editingCountry.country_code === 'CA' && editingCountry.caPostExtras && (
                        <>
                          <div className="form-section-divider" style={{ margin: '30px 0 20px 0', borderTop: '2px solid var(--border-color)', paddingTop: '20px' }}>
                            <h4 style={{ marginBottom: '15px', color: 'var(--primary-color)' }}>⛽ Canada Post Fuel Surcharge</h4>
                            <p style={{ fontSize: '0.9em', color: 'var(--text-secondary)', marginBottom: '10px' }}>
                              Canada Post applies a variable fuel surcharge on top of parcel base rates. This surcharge is updated quarterly.
                              When enabled, the surcharge is added to parcel band base rates at checkout.
                            </p>
                            <p style={{ fontSize: '0.85em', color: 'var(--text-secondary)', fontStyle: 'italic', marginBottom: '15px' }}>
                              This is a temporary feature toggle — turn off when running promotions or if you've already baked the surcharge into band prices.
                            </p>
                          </div>

                          <div className="form-row checkbox-row">
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={(editingCountry.caPostExtras?.fuel_surcharge_pct || 0) > 0}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    handleCaPostExtraChange('fuel_surcharge_pct', 33.5);
                                  } else {
                                    handleCaPostExtraChange('fuel_surcharge_pct', 0);
                                  }
                                }}
                              />
                              <span className="label-text">Enable Fuel Surcharge on Parcel Rates</span>
                              <span className="help-text">
                                When enabled, a fuel surcharge % is added on top of parcel band base rates (not lettermail or flat-rate boxes).
                                Disable to charge only the base band rates.
                              </span>
                            </label>
                          </div>

                          {(editingCountry.caPostExtras?.fuel_surcharge_pct || 0) > 0 && (
                            <div className="form-row">
                              <label>
                                <span className="label-text">⛽ Fuel Surcharge Rate (%):</span>
                                <div className="input-with-prefix">
                                  <input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max="100"
                                    value={editingCountry.caPostExtras?.fuel_surcharge_pct || 33.5}
                                    onChange={(e) => handleCaPostExtraChange('fuel_surcharge_pct', parseFloat(e.target.value) || 0)}
                                    className="rate-input"
                                  />
                                  <span className="suffix">%</span>
                                </div>
                                <span className="help-text">
                                  Current Canada Post fuel surcharge rate (typically 30–35%). Check{' '}
                                  <a href="https://www.canadapost-postescanada.ca/cpc/en/support/kb/sending/rates-prices/fuel-surcharges" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--primary-color)' }}>
                                    Canada Post Fuel Surcharges
                                  </a>{' '}
                                  for the latest rate.
                                </span>
                                {editingCountry.caPostExtras?.fuel_surcharge_pct > 0 && (
                                  <div style={{ marginTop: '10px', padding: '10px', background: 'var(--bg-secondary)', borderRadius: '6px', fontSize: '0.85em' }}>
                                    <strong>Example (2 kg parcel):</strong>{' '}
                                    {(() => {
                                      const bands = editingCountry.caPostExtras?.parcel_bands || [];
                                      const band2kg = [...bands].sort((a, b) => a.weight_kg - b.weight_kg).find(b => b.weight_kg >= 2.0);
                                      if (!band2kg) return 'No 2kg band configured';
                                      const base = band2kg.price_cents;
                                      const fuel = Math.round(base * (editingCountry.caPostExtras?.fuel_surcharge_pct || 0) / 100);
                                      return `Base $${(base / 100).toFixed(2)} + Fuel $${(fuel / 100).toFixed(2)} = $${((base + fuel) / 100).toFixed(2)} (pre-tax)`;
                                    })()}
                                  </div>
                                )}
                              </label>
                            </div>
                          )}
                        </>
                      )}

                      {/* Tariff Configuration - Only for US */}
                      {editingCountry.country_code === 'US' && (
                        <>
                          <div className="form-section-divider" style={{ margin: '30px 0 20px 0', borderTop: '2px solid var(--border-color)', paddingTop: '20px' }}>
                            <h4 style={{ marginBottom: '15px', color: 'var(--primary-color)' }}>🇺🇸 U.S. Tariff Configuration</h4>
                            <p style={{ fontSize: '0.9em', color: 'var(--text-secondary)', marginBottom: '20px' }}>
                              Configure tariff settings for shipments to the United States. Tariffs apply to orders over the exemption threshold.
                            </p>
                          </div>

                          <div className="form-row checkbox-row">
                            <label className="checkbox-label">
                              <input
                                type="checkbox"
                                checked={editingCountry.tariff_enabled}
                                onChange={(e) => handleInputChange('tariff_enabled', e.target.checked)}
                              />
                              <span className="label-text">Enable U.S. Tariff Calculation</span>
                              <span className="help-text">
                                When enabled, tariffs will be calculated and displayed to customers for U.S. orders
                              </span>
                            </label>
                          </div>

                          <div className="form-row">
                            <label>
                              <span className="label-text">💵 Tariff Rate (%):</span>
                              <div className="input-with-prefix">
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  max="100"
                                  value={(editingCountry.tariff_rate * 100).toFixed(2)}
                                  onChange={(e) => handleInputChange('tariff_rate', parseFloat(e.target.value) / 100)}
                                  className="rate-input"
                                  disabled={!editingCountry.tariff_enabled}
                                />
                                <span className="suffix">%</span>
                              </div>
                              <span className="help-text">
                                Tariff percentage applied to order total (default: 35% per Canada Post U.S. policy)
                              </span>
                            </label>
                          </div>

                          <div className="form-row">
                            <label>
                              <span className="label-text">🎁 Exemption Threshold (USD):</span>
                              <div className="input-with-prefix">
                                <span className="prefix">$</span>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={editingCountry.tariff_exemption_threshold_usd}
                                  onChange={(e) => handleInputChange('tariff_exemption_threshold_usd', e.target.value)}
                                  className="rate-input"
                                  disabled={!editingCountry.tariff_enabled}
                                />
                              </div>
                              <span className="help-text">
                                Orders under this amount (in USD) are exempt from tariffs as personal gifts (default: $100 USD)
                              </span>
                            </label>
                          </div>

                          <div className="form-row">
                            <label>
                              <span className="label-text">📝 Tariff Notes:</span>
                              <textarea
                                value={editingCountry.tariff_notes || ''}
                                onChange={(e) => handleInputChange('tariff_notes', e.target.value)}
                                className="tariff-notes-input"
                                rows="3"
                                placeholder="Internal notes about tariff policy and regulations..."
                                disabled={!editingCountry.tariff_enabled}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  borderRadius: '4px',
                                  border: '1px solid var(--border-color)',
                                  fontFamily: 'inherit',
                                  fontSize: '0.9em'
                                }}
                              />
                              <span className="help-text">
                                Optional notes for reference (e.g., policy details, effective dates)
                              </span>
                            </label>
                          </div>
                        </>
                      )}

                      <div className="form-row checkbox-row" style={{ marginTop: '20px' }}>
                        <label className="checkbox-label">
                          <input
                            type="checkbox"
                            checked={editingCountry.enabled}
                            onChange={(e) => handleInputChange('enabled', e.target.checked)}
                          />
                          <span>Enable shipping to this country</span>
                        </label>
                      </div>

                      <div className="form-actions">
                        <button
                          className="btn-cancel"
                          onClick={handleCancel}
                          disabled={saving}
                        >
                          Cancel
                        </button>
                        <button
                          className="btn-save"
                          onClick={handleSave}
                          disabled={saving}
                        >
                          {saving ? 'Saving...' : '✅ Save Changes'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <div className="shipping-rates-display">
                      <div className="rate-item">
                        <span className="rate-label">Standard:</span>
                        <span className="rate-value">${parseFloat(setting.standard_rate).toFixed(2)}</span>
                      </div>
                      <div className="rate-item">
                        <span className="rate-label">Expedited:</span>
                        <span className="rate-value">${parseFloat(setting.expedited_rate).toFixed(2)}</span>
                      </div>
                      <div className="rate-item">
                        <span className="rate-label">Express:</span>
                        <span className="rate-value">${parseFloat(setting.express_rate).toFixed(2)}</span>
                      </div>
                      <div className="free-threshold-display">
                        <span className="free-label">🎁 Free Shipping:</span>
                        <span className="free-value">Orders over ${parseFloat(setting.free_threshold).toFixed(2)}</span>
                      </div>
                      {(() => {
                        // Parse and display weight thresholds
                        let thresholds = null;

                        if (setting.weight_thresholds) {
                          try {
                            thresholds = typeof setting.weight_thresholds === 'string'
                              ? JSON.parse(setting.weight_thresholds)
                              : setting.weight_thresholds;
                          } catch (e) {
                            console.error('Error parsing weight thresholds for', setting.country_code, ':', e, setting.weight_thresholds);
                            return null;
                          }
                        }

                        if (thresholds && typeof thresholds === 'object' && !Array.isArray(thresholds) && thresholds.lettermail_bands?.length) {
                          const sortedThresholds = [...thresholds.lettermail_bands].sort((a, b) => a.weight_kg - b.weight_kg);
                          const dimBoxes = thresholds.flat_rate_boxes || [];
                          const flatsCents = thresholds.flat_rate_boxes_cents || [];
                          return (
                            <div className="weight-thresholds-display">
                              <span className="weight-label">📮 Oversized Lettermail (by weight):</span>
                              <div className="thresholds-list">
                                {sortedThresholds.map((t, i) => (
                                  <div key={i} className="threshold-display-item">
                                    Up to {t.weight_kg}kg: ${(t.price_cents / 100).toFixed(2)}
                                  </div>
                                ))}
                              </div>
                              {thresholds.ca_shipment_longest_cm != null && (
                                <span className="help-text" style={{ display: 'block', marginTop: '8px', fontSize: '0.85em' }}>
                                  📏 Flat-rate choice assumes longest shipped edge ≥ {Number(thresholds.ca_shipment_longest_cm).toFixed(2)} cm (e.g. 14″ legal); smallest <em>fitting</em> box is used vs lettermail.
                                </span>
                              )}
                              {dimBoxes.length > 0 && (
                                <>
                                  <span className="weight-label" style={{ marginTop: '10px', display: 'block' }}>📦 Flat-rate boxes (≤{thresholds.flat_rate_max_kg ?? 5}kg, by max length):</span>
                                  <div className="thresholds-list">
                                    {dimBoxes.map((b, i) => (
                                      <div key={i} className="threshold-display-item">
                                        ${(b.price_cents / 100).toFixed(2)} — up to {b.max_length_cm} cm
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                              {!dimBoxes.length && flatsCents.length > 0 && (
                                <>
                                  <span className="weight-label" style={{ marginTop: '10px', display: 'block' }}>📦 Flat-rate boxes (≤{thresholds.flat_rate_max_kg ?? 5}kg):</span>
                                  <div className="thresholds-list">
                                    {flatsCents.map((c, i) => (
                                      <div key={i} className="threshold-display-item">${(c / 100).toFixed(2)}</div>
                                    ))}
                                  </div>
                                </>
                              )}
                              {thresholds.parcel_bands?.length > 0 && (
                                <>
                                  <span className="weight-label" style={{ marginTop: '10px', display: 'block' }}>📦 Parcel Bands (base rates):</span>
                                  <div className="thresholds-list">
                                    {[...thresholds.parcel_bands].sort((a, b) => a.weight_kg - b.weight_kg).map((b, i) => (
                                      <div key={i} className="threshold-display-item">
                                        Up to {b.weight_kg}kg: ${(b.price_cents / 100).toFixed(2)}
                                      </div>
                                    ))}
                                  </div>
                                </>
                              )}
                              {(thresholds.fuel_surcharge_pct || 0) > 0 && (
                                <div style={{ marginTop: '10px', padding: '8px 12px', background: '#fff3cd', borderRadius: '6px', fontSize: '0.85em', border: '1px solid #ffc107' }}>
                                  <strong>⛽ Fuel Surcharge: {thresholds.fuel_surcharge_pct}%</strong> — applied to parcel band base rates at checkout
                                </div>
                              )}
                              {!thresholds.fuel_surcharge_pct && (
                                <div style={{ marginTop: '10px', padding: '8px 12px', background: '#d4edda', borderRadius: '6px', fontSize: '0.85em', border: '1px solid #28a745' }}>
                                  <strong>⛽ Fuel Surcharge: OFF</strong> — parcel band prices are charged as-is (no surcharge)
                                </div>
                              )}
                            </div>
                          );
                        }

                        if (Array.isArray(thresholds) && thresholds.length > 0) {
                          const sortedThresholds = [...thresholds].sort((a, b) => a.weight_kg - b.weight_kg);

                          return (
                            <div className="weight-thresholds-display">
                              <span className="weight-label">⚖️ Weight Thresholds:</span>
                              <div className="thresholds-list">
                                {sortedThresholds.map((t, i) => (
                                  <div key={i} className="threshold-display-item">
                                    Up to {t.weight_kg}kg: ${(t.price_cents / 100).toFixed(2)}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        }

                        return null;
                      })()}

                      <button
                        className="btn-edit"
                        onClick={() => handleEdit(setting)}
                      >
                        ✏️ Edit Rates
                      </button>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </>
      ) : activeTab === 'flat-rate' ? (
        /* ══════════════════════════════════════════════════════════
           FLAT-RATE SHIPPING TAB
           ══════════════════════════════════════════════════════════ */
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <h3 style={{ margin: '0 0 6px 0' }}>📦 Flat-Rate Shipping Configuration</h3>
              <p style={{ margin: 0, fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                Base shipping &amp; handling per order (highest of applicable item types) + a per-item
                postage adjustment. When enabled, this overrides weight-based shipping for all countries.
              </p>
            </div>
            {!flatRateEditing && (
              <button className="btn-edit" onClick={() => setFlatRateEditing(true)} style={{ whiteSpace: 'nowrap' }}>
                ✏️ Edit
              </button>
            )}
          </div>

          {flatRateEditing && flatRateData ? (
            /* ── Edit Mode ── */
            <div className="shipping-edit-form flat-rate-edit-panel">

              {/* Enable toggle */}
              <div className="form-row checkbox-row" style={{ marginBottom: '16px' }}>
                <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="checkbox"
                    checked={flatRateData.enabled || false}
                    onChange={(e) => handleFlatRateTabChange('enabled', e.target.checked)}
                  />
                  <span style={{ fontWeight: '600', fontSize: '1.05em' }}>
                    {flatRateData.enabled ? '✅ Flat-Rate Shipping is ACTIVE' : '⬜ Flat-Rate Shipping is OFF'}
                  </span>
                </label>
                <p style={{ fontSize: '0.85em', color: 'var(--text-secondary)', marginTop: '4px', marginLeft: '28px' }}>
                  When active, flat-rate takes precedence over weight-based shipping at checkout.
                </p>
              </div>

              {/* Fuel surcharge */}
              <div className="form-row checkbox-row" style={{ marginBottom: '20px', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                <label className="checkbox-label" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="checkbox"
                    checked={(flatRateData.fuel_surcharge_pct || 0) > 0}
                    onChange={(e) => handleFlatRateTabChange('fuel_surcharge_pct', e.target.checked ? 10 : 0)}
                  />
                  <span className="label-text">⛽ Fuel Surcharge</span>
                  {(flatRateData.fuel_surcharge_pct || 0) > 0 && (
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}>
                      <input
                        type="number"
                        step="0.5"
                        min="0"
                        max="100"
                        value={flatRateData.fuel_surcharge_pct || 10}
                        onChange={(e) => handleFlatRateTabChange('fuel_surcharge_pct', parseFloat(e.target.value) || 0)}
                        className="rate-input"
                        style={{ width: '70px' }}
                      />
                      <span>%</span>
                    </div>
                  )}
                </label>
              </div>

              {/* Payment processing fee (Stripe / PayPal) */}
              <div className="form-row checkbox-row" style={{ marginBottom: '20px', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '8px' }}>
                <label className="checkbox-label" style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                  <input
                    type="checkbox"
                    checked={(flatRateData.transaction_fee_pct || 0) > 0}
                    onChange={(e) => handleFlatRateTabChange('transaction_fee_pct', e.target.checked ? 2.9 : 0)}
                    style={{ marginTop: '4px' }}
                  />
                  <span>
                    <span className="label-text">💳 Payment Processing Fee (Stripe / PayPal)</span>
                    {(flatRateData.transaction_fee_pct || 0) > 0 && (
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', marginLeft: '8px' }}>
                        <input
                          type="number"
                          step="0.1"
                          min="0"
                          max="100"
                          value={flatRateData.transaction_fee_pct || 2.9}
                          onChange={(e) => handleFlatRateTabChange('transaction_fee_pct', parseFloat(e.target.value) || 0)}
                          className="rate-input"
                          style={{ width: '70px' }}
                        />
                        <span>%</span>
                      </div>
                    )}
                    <span className="help-text" style={{ display: 'block', marginTop: '6px' }}>
                      When enabled, this percentage is added to the checkout total (subtotal + shipping + tax + tariff) to help cover card/PayPal fees.
                      Typical Stripe/PayPal rates are about 2.9% (fixed per-transaction fees are not included).
                    </span>
                    {(flatRateData.transaction_fee_pct || 0) > 0 && (
                      <div style={{ marginTop: '8px', padding: '8px 10px', background: 'var(--color-background, #fff)', borderRadius: '6px', fontSize: '0.85em' }}>
                        <strong>Example:</strong> $100.00 order → + {(flatRateData.transaction_fee_pct || 0).toFixed(1)}% fee = ${(100 * (1 + (flatRateData.transaction_fee_pct || 0) / 100)).toFixed(2)} charged at checkout
                      </div>
                    )}
                  </span>
                </label>
              </div>

              {/* Destination rate cards */}
              <h4 style={{ margin: '0 0 12px 0' }}>Rates by Destination & Item Type</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px', marginBottom: '20px' }}>
                {['CA', 'US', 'INTL'].map(dest => (
                  <div key={dest} className="dest-rate-card">
                    <h5 style={{ margin: '0 0 12px 0', fontSize: '1em' }}>
                      {dest === 'CA' ? '🇨🇦 Canada' : dest === 'US' ? '🇺🇸 USA' : '🌍 International'}
                    </h5>
                    {['Book', 'CD'].map(itemType => {
                      const rates = flatRateData.destinations?.[dest]?.[itemType] || { handling_cents: 0, per_item_cents: 0, tiers: [] };
                      const tiers = rates.tiers || [];
                      return (
                        <div key={itemType} style={{ marginBottom: '14px' }}>
                          <div style={{ fontWeight: '600', fontSize: '0.9em', marginBottom: '4px' }}>
                            {itemType === 'CD' ? '💿 CD' : '📚 Book'}
                          </div>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85em', marginBottom: '6px' }}>
                            Base shipping &amp; handling $
                            <input
                              type="number" step="0.50" min="0"
                              value={(rates.handling_cents / 100).toFixed(2)}
                              onChange={(e) => handleFlatRateTabChange(`destinations.${dest}.${itemType}.handling_cents`, Math.round(parseFloat(e.target.value || 0) * 100))}
                              className="rate-input" style={{ width: '75px' }}
                            />
                          </label>
                          {/* Quantity tiers */}
                          <div style={{ fontSize: '0.82em', color: 'var(--text-secondary)', marginBottom: '3px' }}>Postage adjustment per-item tiers (tracks postal weight bands):</div>
                          {tiers.map((tier, ti) => (
                            <div key={ti} style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '3px', fontSize: '0.85em' }}>
                              <span style={{ minWidth: '50px' }}>{ti === 0 ? 'Items 1' : `${(tiers[ti - 1]?.up_to || 0) + 1}`}–{tier.up_to || '∞'}</span>
                              <span>$</span>
                              <input
                                type="number" step="0.25" min="0"
                                value={(tier.per_item_cents / 100).toFixed(2)}
                                onChange={(e) => {
                                  const newTiers = [...tiers];
                                  newTiers[ti] = { ...newTiers[ti], per_item_cents: Math.round(parseFloat(e.target.value || 0) * 100) };
                                  handleFlatRateTabChange(`destinations.${dest}.${itemType}.tiers`, newTiers);
                                  handleFlatRateTabChange(`destinations.${dest}.${itemType}.per_item_cents`, newTiers[0]?.per_item_cents || 0);
                                }}
                                className="rate-input" style={{ width: '65px' }}
                              />
                              <span>/ea</span>
                              {tier.up_to !== null && tier.up_to !== undefined && (
                                <label style={{ display: 'flex', alignItems: 'center', gap: '2px', marginLeft: '4px' }}>
                                  cap:
                                  <input
                                    type="number" step="1" min="1"
                                    value={tier.up_to}
                                    onChange={(e) => {
                                      const newTiers = [...tiers];
                                      newTiers[ti] = { ...newTiers[ti], up_to: parseInt(e.target.value) || 1 };
                                      handleFlatRateTabChange(`destinations.${dest}.${itemType}.tiers`, newTiers);
                                    }}
                                    className="rate-input" style={{ width: '45px' }}
                                  />
                                </label>
                              )}
                              <button
                                type="button"
                                onClick={() => {
                                  const newTiers = tiers.filter((_, i) => i !== ti);
                                  handleFlatRateTabChange(`destinations.${dest}.${itemType}.tiers`, newTiers);
                                }}
                                style={{ background: 'none', border: '1px solid #e57373', color: '#e57373', borderRadius: '4px', cursor: 'pointer', padding: '2px 6px', fontSize: '0.85em' }}
                              >✕</button>
                            </div>
                          ))}
                          <div style={{ display: 'flex', gap: '6px', marginTop: '2px' }}>
                            <button
                              type="button"
                              onClick={() => {
                                const lastCap = tiers.length > 0 ? (tiers[tiers.length - 1]?.up_to || 5) : 0;
                                const newTier = { up_to: lastCap + 5, per_item_cents: rates.per_item_cents || 300 };
                                handleFlatRateTabChange(`destinations.${dest}.${itemType}.tiers`, [...tiers, newTier]);
                              }}
                              style={{ fontSize: '0.8em', background: 'none', border: '1px dashed var(--color-success, #4caf50)', color: 'var(--color-success, #4caf50)', borderRadius: '4px', cursor: 'pointer', padding: '2px 8px' }}
                            >+ Add Tier</button>
                            {tiers.length > 0 && !tiers.some(t => t.up_to === null) && (
                              <button
                                type="button"
                                onClick={() => {
                                  handleFlatRateTabChange(`destinations.${dest}.${itemType}.tiers`, [...tiers, { up_to: null, per_item_cents: rates.per_item_cents || 300 }]);
                                }}
                                style={{ fontSize: '0.8em', background: 'none', border: '1px dashed #2196f3', color: '#2196f3', borderRadius: '4px', cursor: 'pointer', padding: '2px 8px' }}
                              >+ Add ∞ Tier</button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>

              {/* Live calculator — all destinations */}
              <div className="live-calculator-panel">
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  <strong style={{ fontSize: '1em' }}>Live Calculator</strong>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9em' }}>
                    📚 Books
                    <button type="button" className="qty-step-btn" onClick={() => setExampleBooks(Math.max(0, exampleBooks - 1))}>−</button>
                    <span style={{ fontWeight: '700', fontSize: '1.1em', minWidth: '20px', textAlign: 'center' }}>{exampleBooks}</span>
                    <button type="button" className="qty-step-btn" onClick={() => setExampleBooks(exampleBooks + 1)}>+</button>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.9em' }}>
                    💿 CDs
                    <button type="button" className="qty-step-btn" onClick={() => setExampleCds(Math.max(0, exampleCds - 1))}>−</button>
                    <span style={{ fontWeight: '700', fontSize: '1.1em', minWidth: '20px', textAlign: 'center' }}>{exampleCds}</span>
                    <button type="button" className="qty-step-btn" onClick={() => setExampleCds(exampleCds + 1)}>+</button>
                  </label>
                  {(exampleBooks + exampleCds > 0) && (
                    <>
                      <span style={{ fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                        💰 Est. cart:{' '}
                        <strong style={{ color: 'var(--color-text-primary)' }}>
                          ${estimateCartMerchandise(exampleBooks, exampleCds).toFixed(2)}
                        </strong>
                        <span style={{ fontSize: '0.85em', marginLeft: '6px' }}>
                          ({[
                            exampleBooks > 0 ? `${exampleBooks}×$${ESTIMATE_BOOK_PRICE.toFixed(2)}` : null,
                            exampleCds > 0 ? `${exampleCds}×$${ESTIMATE_CD_PRICE.toFixed(2)}` : null
                          ].filter(Boolean).join(' + ')})
                        </span>
                      </span>
                      <span style={{ fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                        ⚖️ Est. weight:{' '}
                        <strong style={{ color: 'var(--color-text-primary)' }}>
                          {estimateCartWeightKg(exampleBooks, exampleCds).toFixed(2)} kg
                        </strong>
                        <span style={{ fontSize: '0.85em', marginLeft: '6px' }}>
                          ({[
                            exampleBooks > 0 ? `${exampleBooks}×${ESTIMATE_BOOK_KG} kg` : null,
                            exampleCds > 0 ? `${exampleCds}×${ESTIMATE_CD_KG} kg` : null
                          ].filter(Boolean).join(' + ')})
                        </span>
                      </span>
                    </>
                  )}
                </div>

                {(exampleBooks + exampleCds === 0) ? (
                  <p style={{ color: 'var(--text-secondary)', fontSize: '0.9em', margin: 0 }}>Add at least 1 book or CD to see the calculation.</p>
                ) : (
                  (() => {
                    const merchTotal = estimateCartMerchandise(exampleBooks, exampleCds);
                    return (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px' }}>
                    {[['CA', '🇨🇦 Canada'], ['US', '🇺🇸 USA'], ['INTL', '🌍 International']].map(([destKey, destLabel]) => {
                      const destRates = flatRateData.destinations?.[destKey];
                      if (!destRates) return null;

                      const tierCalc = (qty, typeRates) => {
                        if (qty <= 0) return { total: 0, desc: '—' };
                        const tiers = typeRates?.tiers;
                        if (!tiers?.length) return { total: (typeRates?.per_item_cents || 0) * qty, desc: `${qty}×$${((typeRates?.per_item_cents || 0)/100).toFixed(2)}` };
                        const sorted = [...tiers].sort((a, b) => (a.up_to ?? Infinity) - (b.up_to ?? Infinity));
                        let rem = qty, prev = 0, total = 0, parts = [];
                        for (const t of sorted) {
                          if (rem <= 0) break;
                          const slot = (t.up_to ?? Infinity) - prev;
                          const n = Math.min(rem, slot);
                          total += n * (t.per_item_cents || 0);
                          parts.push(`${n}×$${((t.per_item_cents||0)/100).toFixed(2)}`);
                          rem -= n; prev = t.up_to ?? Infinity;
                        }
                        if (rem > 0) { total += rem * (sorted[sorted.length-1].per_item_cents||0); parts.push(`${rem}×$${((sorted[sorted.length-1].per_item_cents||0)/100).toFixed(2)}`); }
                        return { total, desc: parts.join(' + ') };
                      };

                      const bookH = destRates.Book?.handling_cents || 0;
                      const cdH = destRates.CD?.handling_cents || 0;
                      const maxH = Math.max(exampleBooks > 0 ? bookH : 0, exampleCds > 0 ? cdH : 0);
                      const maxType = (exampleBooks > 0 ? bookH : 0) >= (exampleCds > 0 ? cdH : 0) ? 'Book' : 'CD';
                      const bookCalc = tierCalc(exampleBooks, destRates.Book);
                      const cdCalc = tierCalc(exampleCds, destRates.CD);
                      const sub = maxH + bookCalc.total + cdCalc.total;
                      const fuelPct = flatRateData.fuel_surcharge_pct || 0;
                      const fuelAmt = fuelPct > 0 ? Math.round(sub * fuelPct / 100) : 0;
                      const total = sub + fuelAmt;

                      return (
                        <div key={destKey} className="calc-dest-card">
                          <div style={{ fontWeight: '700', marginBottom: '4px' }}>{destLabel}</div>
                          <div>${(maxH / 100).toFixed(2)} <span style={{ color: 'var(--text-secondary)' }}>({maxType} base)</span></div>
                          {exampleBooks > 0 && <div>+ Books: {bookCalc.desc} = ${(bookCalc.total / 100).toFixed(2)}</div>}
                          {exampleCds > 0 && <div>+ CDs: {cdCalc.desc} = ${(cdCalc.total / 100).toFixed(2)}</div>}
                          {fuelAmt > 0 && <div>+ Fuel {fuelPct}%: ${(fuelAmt / 100).toFixed(2)}</div>}
                          <div style={{ fontWeight: '700', borderTop: '1px solid var(--color-border)', paddingTop: '4px', marginTop: '4px' }}>
                            Shipping: ${(total / 100).toFixed(2)}
                          </div>
                          <div style={{ fontWeight: '700', color: 'var(--color-primary)' }}>
                            Order est.: ${(merchTotal + total / 100).toFixed(2)}
                            <span style={{ fontWeight: '400', fontSize: '0.9em', color: 'var(--text-secondary)' }}> (cart + ship)</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                    );
                  })()
                )}
              </div>

              {/* Formula explanation */}
              <div className="formula-note">
                <strong>How it works:</strong> The highest base shipping &amp; handling amount among item types is charged once.
                The per-item postage adjustment uses <strong>quantity tiers</strong> — e.g. items 1–2 at $3/ea, items 3–5 at $6/ea, 6+ at $5/ea.
                This lets you track postal weight bands more closely. Digital products and checkout free gifts are excluded.
                If fuel surcharge is on, it's applied on top of the subtotal.
                Payment processing fee (if enabled) applies to the full checkout total, not shipping alone.
              </div>

              {/* Save / Cancel */}
              <div className="form-actions" style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button className="btn-cancel" onClick={() => { setFlatRateEditing(false); fetchShippingSettings(); }} disabled={saving}>
                  Cancel
                </button>
                <button className="btn-save" onClick={handleFlatRateSave} disabled={saving}>
                  {saving ? 'Saving...' : '✅ Save Flat-Rate Settings'}
                </button>
              </div>
            </div>
          ) : (
            /* ── Read-only Mode ── */
            <div className="flat-rate-readonly-panel">
              {/* Status banner */}
              <div className={flatRateData?.enabled ? 'status-banner-enabled' : 'status-banner-disabled'}>
                <span style={{ fontSize: '1.5em' }}>{flatRateData?.enabled ? '✅' : '⬜'}</span>
                <div>
                  <strong style={{ fontSize: '1.05em' }}>
                    {flatRateData?.enabled ? 'Flat-Rate Shipping is ACTIVE' : 'Flat-Rate Shipping is OFF'}
                  </strong>
                  <div className="muted-text" style={{ fontSize: '0.85em', marginTop: '2px' }}>
                    {flatRateData?.enabled
                      ? 'Customers are charged base shipping & handling + a per-item postage adjustment (weight-based shipping is overridden).'
                      : 'Weight-based shipping is currently in use. Enable flat-rate to switch.'}
                  </div>
                </div>
              </div>

              {flatRateData?.fuel_surcharge_pct > 0 && (
                <div className="fuel-notice">
                  ⛽ <strong>Fuel Surcharge: {flatRateData.fuel_surcharge_pct}%</strong> applied on top of all flat-rate charges.
                </div>
              )}

              {(flatRateData?.transaction_fee_pct || 0) > 0 && (
                <div className="fuel-notice" style={{ background: '#eff6ff', borderColor: '#93c5fd' }}>
                  💳 <strong>Payment processing fee: {flatRateData.transaction_fee_pct}%</strong> added to checkout totals (Stripe / PayPal pass-through).
                </div>
              )}

              {/* Rate cards (read-only) */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '14px' }}>
                {['CA', 'US', 'INTL'].map(dest => {
                  const d = flatRateData?.destinations?.[dest];
                  if (!d) return null;
                  return (
                    <div key={dest} className="dest-rate-card" style={{ padding: '14px' }}>
                      <h5 style={{ margin: '0 0 10px 0' }}>
                        {dest === 'CA' ? '🇨🇦 Canada' : dest === 'US' ? '🇺🇸 USA' : '🌍 International'}
                      </h5>
                      {['Book', 'CD'].map(type => {
                        const r = d[type];
                        if (!r) return null;
                        const tiers = r.tiers || [];
                        return (
                          <div key={type} style={{ marginBottom: '8px', fontSize: '0.9em' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span>{type === 'CD' ? '💿' : '📚'} {type}:</span>
                              <span>${((r.handling_cents || 0) / 100).toFixed(2)} base</span>
                            </div>
                            {tiers.length > 0 ? (
                              <div style={{ paddingLeft: '16px', fontSize: '0.85em', color: 'var(--text-secondary)' }}>
                                {tiers.map((t, i) => {
                                  const from = i === 0 ? 1 : (tiers[i-1]?.up_to || 0) + 1;
                                  const to = t.up_to ?? '∞';
                                  return <div key={i}>{from}–{to} items: ${((t.per_item_cents || 0) / 100).toFixed(2)}/ea</div>;
                                })}
                              </div>
                            ) : (
                              <div style={{ paddingLeft: '16px', fontSize: '0.85em', color: 'var(--text-secondary)' }}>
                                ${((r.per_item_cents || 0) / 100).toFixed(2)}/item
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      ) : activeTab === 'exchange-rates' ? (
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <div>
              <h3 style={{ margin: '0 0 6px 0' }}>💱 Reference Exchange Rates</h3>
              <p style={{ margin: 0, fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                Approximate CAD conversion rates shown in cart and checkout by customer region.
                US → USD; UK/Scotland → GBP; Australia → AUD; other international → EUR.
                Orders are still charged in CAD. The USD rate is also used for US tariff threshold calculations.
              </p>
            </div>
            {!currencyRatesEditing && (
              <button type="button" className="btn-edit" onClick={handleCurrencyRatesEdit} style={{ whiteSpace: 'nowrap' }}>
                ✏️ Edit
              </button>
            )}
          </div>

          <div className="shipping-card" style={{ maxWidth: '720px', width: '100%' }}>
            {currencyRatesEditing ? (
              <div className="shipping-edit-form">
                <div className="form-row">
                  <label>
                    <span className="label-text">CAD → USD (1 CAD = ? USD)</span>
                    <input
                      type="number"
                      step="0.0001"
                      min="0.01"
                      max="2"
                      value={currencyRatesDraft.cadToUsdRate}
                      onChange={(e) => setCurrencyRatesDraft((prev) => ({ ...prev, cadToUsdRate: e.target.value }))}
                      className="rate-input"
                    />
                    <span className="help-text">
                      Example: 0.74 means $100 CAD ≈ $74 USD. Used for US cart display and tariff conversion.
                    </span>
                  </label>
                </div>
                <div className="form-row">
                  <label>
                    <span className="label-text">CAD → EUR (1 CAD = ? EUR)</span>
                    <input
                      type="number"
                      step="0.0001"
                      min="0.01"
                      max="2"
                      value={currencyRatesDraft.cadToEurRate}
                      onChange={(e) => setCurrencyRatesDraft((prev) => ({ ...prev, cadToEurRate: e.target.value }))}
                      className="rate-input"
                    />
                    <span className="help-text">
                      Example: 0.68 means $100 CAD ≈ €68 EUR. Default for Ireland, Europe, and other international countries.
                    </span>
                  </label>
                </div>
                <div className="form-row">
                  <label>
                    <span className="label-text">CAD → GBP (1 CAD = ? GBP)</span>
                    <input
                      type="number"
                      step="0.0001"
                      min="0.01"
                      max="2"
                      value={currencyRatesDraft.cadToGbpRate}
                      onChange={(e) => setCurrencyRatesDraft((prev) => ({ ...prev, cadToGbpRate: e.target.value }))}
                      className="rate-input"
                    />
                    <span className="help-text">
                      Example: 0.57 means $100 CAD ≈ £57 GBP. Shown for United Kingdom and Scotland addresses.
                    </span>
                  </label>
                </div>
                <div className="form-row">
                  <label>
                    <span className="label-text">CAD → AUD (1 CAD = ? AUD)</span>
                    <input
                      type="number"
                      step="0.0001"
                      min="0.01"
                      max="3"
                      value={currencyRatesDraft.cadToAudRate}
                      onChange={(e) => setCurrencyRatesDraft((prev) => ({ ...prev, cadToAudRate: e.target.value }))}
                      className="rate-input"
                    />
                    <span className="help-text">
                      Example: 1.10 means $100 CAD ≈ $110 AUD. Shown for Australia addresses.
                    </span>
                  </label>
                </div>
                <div className="form-actions">
                  <button type="button" className="btn-save" onClick={handleCurrencyRatesSave} disabled={saving}>
                    {saving ? 'Saving…' : '💾 Save Rates'}
                  </button>
                  <button type="button" className="btn-cancel" onClick={handleCurrencyRatesCancel} disabled={saving}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="shipping-display">
                <div className="rate-display-row">
                  <span className="rate-label">CAD → USD</span>
                  <span className="rate-value">{Number(currencyRates.cadToUsdRate).toFixed(4)}</span>
                </div>
                <div className="rate-display-row">
                  <span className="rate-label">CAD → EUR</span>
                  <span className="rate-value">{Number(currencyRates.cadToEurRate).toFixed(4)}</span>
                </div>
                <div className="rate-display-row">
                  <span className="rate-label">CAD → GBP</span>
                  <span className="rate-value">{Number(currencyRates.cadToGbpRate).toFixed(4)}</span>
                </div>
                <div className="rate-display-row">
                  <span className="rate-label">CAD → AUD</span>
                  <span className="rate-value">{Number(currencyRates.cadToAudRate).toFixed(4)}</span>
                </div>
                <div style={{ marginTop: '1rem', fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                  $100.00 CAD reference: ~${(100 * currencyRates.cadToUsdRate).toFixed(2)} USD / ~€{(100 * currencyRates.cadToEurRate).toFixed(2)} EUR / ~£{(100 * currencyRates.cadToGbpRate).toFixed(2)} GBP / ~${(100 * currencyRates.cadToAudRate).toFixed(2)} AUD
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <>
        <div className="shipping-info-box" style={{ marginBottom: '1.25rem' }}>
          <h4>🇨🇦 Combined sales tax by destination (reference)</h4>
          <p style={{ marginBottom: '0.75rem', fontSize: '0.95em' }}>
            The <strong>General rate</strong> in the table below is this combined rate for typical merchandise.
            <strong> Books</strong> often use 5% (point-of-sale rebate); <strong>shipping tax</strong> can differ (e.g. BC often 5% on freight).
          </p>
          <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--background-secondary)', textAlign: 'left' }}>
                <th style={{ padding: '10px', borderBottom: '2px solid var(--border-color)' }}>Destination province / territory</th>
                <th style={{ padding: '10px', borderBottom: '2px solid var(--border-color)' }}>Tax type</th>
                <th style={{ padding: '10px', borderBottom: '2px solid var(--border-color)' }}>Combined rate</th>
              </tr>
            </thead>
            <tbody>
              {[
                { dest: 'AB, NT, NU, YT', type: 'GST only', rate: '5%' },
                { dest: 'BC, MB', type: 'GST + PST', rate: '12%' },
                { dest: 'SK', type: 'GST + PST', rate: '11%' },
                { dest: 'ON', type: 'HST', rate: '13%' },
                { dest: 'NS', type: 'HST', rate: '14%' },
                { dest: 'NB, NL, PE', type: 'HST', rate: '15%' },
                { dest: 'QC', type: 'GST + QST', rate: '14.975%' }
              ].map((row) => (
                <tr key={row.dest} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  <td style={{ padding: '10px' }}>{row.dest}</td>
                  <td style={{ padding: '10px' }}>{row.type}</td>
                  <td style={{ padding: '10px' }}>{row.rate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="tax-settings-container">
          <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', marginTop: '1rem' }}>
            <thead>
              <tr style={{ background: 'var(--background-secondary)', textAlign: 'left' }}>
                <th style={{ padding: '10px', borderBottom: '2px solid var(--border-color)' }}>Zone / Province</th>
                <th style={{ padding: '10px', borderBottom: '2px solid var(--border-color)' }}>General Rate</th>
                <th style={{ padding: '10px', borderBottom: '2px solid var(--border-color)' }}>Books Rate</th>
                <th style={{ padding: '10px', borderBottom: '2px solid var(--border-color)' }}>Shipping Tax</th>
                <th style={{ padding: '10px', borderBottom: '2px solid var(--border-color)' }}>Notes</th>
                <th style={{ padding: '10px', borderBottom: '2px solid var(--border-color)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {taxSettings.map(tax => (
                <tr key={tax.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                  {editingTax?.id === tax.id ? (
                    <>
                      <td style={{ padding: '10px' }}>
                        <strong>{tax.province_name}</strong>
                        <div style={{ fontSize: '0.8em', color: '#666' }}>{tax.zone} {tax.province_code ? `(${tax.province_code})` : ''}</div>
                      </td>
                      <td style={{ padding: '10px' }}>
                        <input
                          type="number"
                          step="0.001"
                          value={editingTax.general_rate}
                          onChange={(e) => handleTaxInputChange('general_rate', e.target.value)}
                          style={{ width: '80px', padding: '4px' }}
                        />
                      </td>
                      <td style={{ padding: '10px' }}>
                        <input
                          type="number"
                          step="0.001"
                          value={editingTax.books_rate}
                          onChange={(e) => handleTaxInputChange('books_rate', e.target.value)}
                          style={{ width: '80px', padding: '4px' }}
                        />
                      </td>
                      <td style={{ padding: '10px' }}>
                        <input
                          type="number"
                          step="0.001"
                          value={editingTax.shipping_tax_rate}
                          onChange={(e) => handleTaxInputChange('shipping_tax_rate', e.target.value)}
                          style={{ width: '80px', padding: '4px' }}
                        />
                      </td>
                      <td style={{ padding: '10px' }}>
                        <input
                          type="text"
                          value={editingTax.notes || ''}
                          onChange={(e) => handleTaxInputChange('notes', e.target.value)}
                          style={{ width: '100%', padding: '4px' }}
                        />
                      </td>
                      <td style={{ padding: '10px' }}>
                        <button onClick={handleTaxSave} disabled={saving} style={{ marginRight: '5px', cursor: 'pointer' }}>💾</button>
                        <button onClick={handleTaxCancel} disabled={saving} style={{ cursor: 'pointer' }}>❌</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={{ padding: '10px' }}>
                        <strong>{tax.province_name}</strong>
                        <div style={{ fontSize: '0.8em', color: '#666' }}>{tax.zone} {tax.province_code ? `(${tax.province_code})` : ''}</div>
                      </td>
                      <td style={{ padding: '10px' }}>{(tax.general_rate * 100).toFixed(3)}%</td>
                      <td style={{ padding: '10px' }}>{(tax.books_rate * 100).toFixed(3)}%</td>
                      <td style={{ padding: '10px' }}>{(tax.shipping_tax_rate * 100).toFixed(3)}%</td>
                      <td style={{ padding: '10px' }}>{tax.notes}</td>
                      <td style={{ padding: '10px' }}>
                        <button onClick={() => handleTaxEdit(tax)} style={{ cursor: 'pointer', border: 'none', background: 'transparent', fontSize: '1.2em' }}>✏️</button>
                      </td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}

      <div className="shipping-info-box">
        <h4>ℹ️ About Shipping Settings</h4>
        <ul>
          <li><strong>3 Shipping Zones:</strong> Canada 🇨🇦, USA 🇺🇸, and International 🌍 (all other countries)</li>
          <li><strong>Standard Shipping:</strong> Regular delivery (5-7 business days)</li>
          <li><strong>Expedited Shipping:</strong> Faster delivery (2-3 business days)</li>
          <li><strong>Express Shipping:</strong> Next-day or 1-2 day delivery</li>
          <li><strong>Free Threshold:</strong> When cart total exceeds this amount, shipping is FREE</li>
          <li><strong>Product Minimums:</strong> Individual products can set minimum shipping costs (configured per product)</li>
          <li><strong>Currency:</strong> All shipping rates are in Canadian Dollars (CAD). Reference USD/EUR amounts for customers are configured under the Exchange Rates tab.</li>
        </ul>
      </div>

      {/* U.S. Tariff Information Box */}
      <div className="shipping-info-box" style={{ marginTop: '2rem', background: '#fff3cd', borderColor: '#ffc107' }}>
        <h4>🇺🇸 U.S. Tariff Policy (Effective 2025)</h4>
        <p style={{ marginBottom: '1rem' }}>
          <strong>Important:</strong> The United States has suspended the US$800 duty-free threshold for goods shipped via international postal network (Canada Post).
        </p>
        <ul>
          <li><strong>Tariff Rate:</strong> 35% ad valorem tariff on most commercial goods from Canada</li>
          <li><strong>Prepayment Required:</strong> Duties must be assessed and prepaid before shipment via Canada Post</li>
          <li><strong>Exemption Threshold:</strong> Personal gifts under US$100 sent by individuals (not businesses) are duty-free</li>
          <li><strong>Documents Exempt:</strong> Documents are not subject to tariffs</li>
          <li><strong>CUSMA Note:</strong> While CUSMA-certified goods can be exempt from tariffs, postal shipments currently require prepayment regardless</li>
          <li><strong>Application:</strong> Our system treats all customers as private individuals for the $100 exemption threshold</li>
        </ul>
      </div>

      {/* Canadian Tax Rules Summary */}
      <div className="shipping-info-box" style={{ marginTop: '2rem' }}>
        <h4>📋 Canadian Tax Rules Summary (Effective April 1, 2025)</h4>

        <h5 style={{ marginTop: '1.5rem', color: 'var(--color-primary)' }}>1. Atlantic Canada</h5>
        <p style={{ fontStyle: 'italic', marginBottom: '1rem' }}>
          <strong>Rules for:</strong> Nova Scotia, New Brunswick, PEI, Newfoundland & Labrador<br />
          In the Atlantic provinces, Books receive a "Point-of-Sale Rebate," meaning the provincial portion of the HST is instantly credited back to the customer, leaving only the 5% federal portion.
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '2rem' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--color-background)', borderBottom: '2px solid var(--color-border)' }}>
              <th style={{ padding: '10px', textAlign: 'left' }}>Province</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Merchandise</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Books</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Shipping</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '10px' }}>Nova Scotia</td>
              <td style={{ padding: '10px' }}>14% HST</td>
              <td style={{ padding: '10px' }}>5% (GST Only)</td>
              <td style={{ padding: '10px' }}>14% HST</td>
              <td style={{ padding: '10px', fontSize: '0.9em' }}>NS rate decreased to 14% on April 1, 2025</td>
            </tr>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '10px' }}>New Brunswick</td>
              <td style={{ padding: '10px' }}>15% HST</td>
              <td style={{ padding: '10px' }}>5% (GST Only)</td>
              <td style={{ padding: '10px' }}>15% HST</td>
              <td style={{ padding: '10px' }}>—</td>
            </tr>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '10px' }}>PEI</td>
              <td style={{ padding: '10px' }}>15% HST</td>
              <td style={{ padding: '10px' }}>5% (GST Only)</td>
              <td style={{ padding: '10px' }}>15% HST</td>
              <td style={{ padding: '10px' }}>—</td>
            </tr>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '10px' }}>Newfoundland</td>
              <td style={{ padding: '10px' }}>15% HST</td>
              <td style={{ padding: '10px' }}>5% (GST Only)</td>
              <td style={{ padding: '10px' }}>15% HST</td>
              <td style={{ padding: '10px' }}>—</td>
            </tr>
          </tbody>
        </table>

        <h5 style={{ marginTop: '1.5rem', color: 'var(--color-primary)' }}>2. Central Canada</h5>
        <p style={{ fontStyle: 'italic', marginBottom: '1rem' }}>
          <strong>Rules for:</strong> Ontario, Quebec
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '2rem' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--color-background)', borderBottom: '2px solid var(--color-border)' }}>
              <th style={{ padding: '10px', textAlign: 'left' }}>Province</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Merchandise</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Books</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Shipping</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '10px' }}>Ontario</td>
              <td style={{ padding: '10px' }}>13% HST</td>
              <td style={{ padding: '10px' }}>5% (GST Only)</td>
              <td style={{ padding: '10px' }}>13% HST</td>
              <td style={{ padding: '10px', fontSize: '0.9em' }}>Ontario provides a Point-of-Sale rebate on qualifying books</td>
            </tr>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '10px' }}>Quebec</td>
              <td style={{ padding: '10px' }}>14.975% (GST+QST)</td>
              <td style={{ padding: '10px' }}>5% (GST Only)</td>
              <td style={{ padding: '10px' }}>14.975%</td>
              <td style={{ padding: '10px', fontSize: '0.9em' }}>Books are "Zero-Rated" for QST purposes. Shipping is fully taxable</td>
            </tr>
          </tbody>
        </table>

        <h5 style={{ marginTop: '1.5rem', color: 'var(--color-primary)' }}>3. Western Canada</h5>
        <p style={{ fontStyle: 'italic', marginBottom: '1rem' }}>
          <strong>Rules for:</strong> BC, Alberta, Saskatchewan, Manitoba<br />
          Western provinces generally have a separate PST. While Merchandise attracts both GST and PST, books are usually exempt from the PST portion. British Columbia has a special exception for shipping.
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '2rem' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--color-background)', borderBottom: '2px solid var(--color-border)' }}>
              <th style={{ padding: '10px', textAlign: 'left' }}>Province</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Merchandise</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Books</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Shipping</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '10px' }}>British Columbia</td>
              <td style={{ padding: '10px' }}>12% (5% GST + 7% PST)</td>
              <td style={{ padding: '10px' }}>5% (GST Only)</td>
              <td style={{ padding: '10px' }}>5% (GST Only)</td>
              <td style={{ padding: '10px', fontSize: '0.9em' }}>Exception: Shipping charges are exempt from BC PST</td>
            </tr>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '10px' }}>Alberta</td>
              <td style={{ padding: '10px' }}>5% (GST Only)</td>
              <td style={{ padding: '10px' }}>5% (GST Only)</td>
              <td style={{ padding: '10px' }}>5% (GST Only)</td>
              <td style={{ padding: '10px', fontSize: '0.9em' }}>No provincial sales tax</td>
            </tr>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '10px' }}>Saskatchewan</td>
              <td style={{ padding: '10px' }}>11% (5% GST + 6% PST)</td>
              <td style={{ padding: '10px' }}>5% (GST Only)</td>
              <td style={{ padding: '10px' }}>11%</td>
              <td style={{ padding: '10px', fontSize: '0.9em' }}>Shipping is taxable</td>
            </tr>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '10px' }}>Manitoba</td>
              <td style={{ padding: '10px' }}>12% (5% GST + 7% PST)</td>
              <td style={{ padding: '10px' }}>5% (GST Only)</td>
              <td style={{ padding: '10px' }}>12%</td>
              <td style={{ padding: '10px', fontSize: '0.9em' }}>Shipping is taxable</td>
            </tr>
          </tbody>
        </table>

        <h5 style={{ marginTop: '1.5rem', color: 'var(--color-primary)' }}>4. The Territories</h5>
        <p style={{ fontStyle: 'italic', marginBottom: '1rem' }}>
          <strong>Rules for:</strong> Yukon, Northwest Territories, Nunavut<br />
          These regions function like Alberta; there is no territorial sales tax, only the federal GST.
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '2rem' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--color-background)', borderBottom: '2px solid var(--color-border)' }}>
              <th style={{ padding: '10px', textAlign: 'left' }}>Region</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Merchandise</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Books</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Shipping</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '10px' }}>All Territories</td>
              <td style={{ padding: '10px' }}>5% (GST Only)</td>
              <td style={{ padding: '10px' }}>5% (GST Only)</td>
              <td style={{ padding: '10px' }}>5% (GST Only)</td>
              <td style={{ padding: '10px' }}>—</td>
            </tr>
          </tbody>
        </table>

        <h5 style={{ marginTop: '1.5rem', color: 'var(--color-primary)' }}>5. International & USA</h5>
        <p style={{ fontStyle: 'italic', marginBottom: '1rem' }}>
          <strong>Rules for:</strong> Exports outside of Canada<br />
          Goods shipped outside of Canada are considered "Zero-Rated Exports."
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '2rem' }}>
          <thead>
            <tr style={{ backgroundColor: 'var(--color-background)', borderBottom: '2px solid var(--color-border)' }}>
              <th style={{ padding: '10px', textAlign: 'left' }}>Destination</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Merchandise</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Books</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Shipping</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Notes</th>
            </tr>
          </thead>
          <tbody>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '10px' }}>USA</td>
              <td style={{ padding: '10px' }}>0% (Tax Free)</td>
              <td style={{ padding: '10px' }}>0% (Tax Free)</td>
              <td style={{ padding: '10px' }}>0% (Tax Free)</td>
              <td style={{ padding: '10px', fontSize: '0.9em' }}>Customer pays local duties/taxes upon receipt</td>
            </tr>
            <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
              <td style={{ padding: '10px' }}>International</td>
              <td style={{ padding: '10px' }}>0% (Tax Free)</td>
              <td style={{ padding: '10px' }}>0% (Tax Free)</td>
              <td style={{ padding: '10px' }}>0% (Tax Free)</td>
              <td style={{ padding: '10px', fontSize: '0.9em' }}>Customer pays local VAT/duties upon receipt</td>
            </tr>
          </tbody>
        </table>

        <div style={{ backgroundColor: 'color-mix(in srgb, var(--color-warning) 10%, transparent)', padding: '1rem', borderRadius: '8px', border: '2px solid var(--color-warning)', marginTop: '1.5rem' }}>
          <h5 style={{ marginTop: 0, color: 'var(--color-warning)' }}>💡 Why is Shipping Taxed?</h5>
          <ul style={{ marginBottom: 0 }}>
            <li>Shipping is considered a service by the CRA.</li>
            <li><strong>General Rule:</strong> If the product is taxable, the shipping service to get it there is also taxable at the destination's rate.</li>
            <li><strong>The BC Exception:</strong> British Columbia specifically exempts freight/delivery charges from their Provincial Sales Tax (PST).</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

// Helper function to get country flag emoji
const getCountryFlag = (countryCode) => {
  const flags = {
    'CA': '🇨🇦',
    'US': '🇺🇸',
    'INTL': '🌍'
  };
  return flags[countryCode] || '🌍';
};

export default AdminShipping;

